import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box, Typography, ToggleButton, ToggleButtonGroup,
  Button, Avatar, Tooltip, Paper, IconButton,
  Snackbar, Alert, Skeleton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeCanvas } from 'qrcode.react';
import { IconContext } from 'react-icons';
import { FaWhatsapp, FaTelegram, FaFacebook, FaLinkedinIn, FaRedditAlien } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { MdEmail, MdDownload, MdOutlineQrCode2, MdLink, MdLockOutline } from 'react-icons/md';
import { FiSmartphone, FiMonitor } from 'react-icons/fi';
import { TbLink } from "react-icons/tb";
import { useProfile } from 'context/ProfileContext';
import { useAuth } from 'context/AuthContext';
import { getAvatarUrl, getContrastRatio, getRgbLuminance } from 'utils';
import ProfileView from 'Components/ProfileView/ProfileView';
import PreviewStatus from 'Components/PreviewStatus/PreviewStatus';
import { PUBLIC_PROFILE_BASE, SHARE_NETWORKS, QR_CONTRAST_MIN_RATIO, QR_FALLBACK_BG, QR_FALLBACK_FG } from 'utils/constants';
import { STATUS } from 'utils/enums';
import logoUrl from 'assets/images/linkerto_logo_qr.png';
import logoUrlDark from 'assets/images/linkerto_logo_dark_qr.png';
import '../../../Components/LivePreview/LivePreview.css';
import './Preview.css';

const QR_STORAGE_KEY = 'linkerto_preview_qr_shown';

type DeviceMode = 'mobile' | 'desktop';

interface ProfileInfo {
  title?: string;
  description?: string;
}

interface ProfileTheme {
  background?: {
    type?: 'solid' | 'gradient';
    solid?: string;
    gradient?: { left?: string; right?: string; direction?: string };
  };
  text?: { primary?: string };
}

const NETWORK_ICONS = {
  whatsapp: <FaWhatsapp />,
  twitter:  <FaXTwitter />,
  telegram: <FaTelegram />,
  facebook: <FaFacebook />,
  linkedin: <FaLinkedinIn />,
  reddit:   <FaRedditAlien />,
  email:    <MdEmail />,
};

const buildShareUrl = (network: string, url: string, text: string): string => {
  const enc = encodeURIComponent;
  switch (network) {
    case 'whatsapp': return `https://wa.me/?text=${enc(text)}`;
    case 'twitter':  return `https://twitter.com/intent/tweet?text=${enc(text)}`;
    case 'telegram': return `https://t.me/share/url?url=${enc(url)}&text=${enc(text)}`;
    case 'facebook': return `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`;
    case 'linkedin': return `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`;
    case 'reddit':   return `https://reddit.com/submit?url=${enc(url)}&title=${enc(text)}`;
    case 'email':    return `mailto:?subject=${enc(text)}&body=${enc(url)}`;
    default:         return url;
  }
};

const Preview = () => {
  const { state, pending, hasChanges, deploy, loading } = useProfile();
  const { user: { username, userImage, deletedAt } } = useAuth();
  const isInDeletion = !!deletedAt;
  const { t } = useTranslation();

  const [device, setDevice]           = useState<DeviceMode>('mobile');
  const [copied, setCopied]           = useState(false);
  const [qrVisible, setQrVisible]     = useState(() => localStorage.getItem(QR_STORAGE_KEY) === 'true');
  const [profileStatus, setProfileStatus] = useState<STATUS>(STATUS.DEFAULT);

  const links = state.currentProfile.links;
  const theme = state.currentProfile.theme as ProfileTheme;
  const info  = state.currentProfile.info as ProfileInfo;

  const avatarSrc = useMemo(() => {
    if (!userImage?.fileName) return null;
    return getAvatarUrl(userImage);
  }, [userImage]);

  useEffect(() => {
    if (loading) return;
    if (state.isPublished) {
      const isDirty = JSON.stringify(state.lastProfile) !== JSON.stringify(state.currentProfile);
      setProfileStatus(isDirty ? STATUS.DRAFT : STATUS.PUBLISHED);
    } else {
      setProfileStatus(STATUS.NOT_PUBLISHED);
    }
  }, [state, loading]);

  const profileUrl = username ? `https://${PUBLIC_PROFILE_BASE}/${username}` : '';
  const shareUrl   = username ? `${PUBLIC_PROFILE_BASE}/${username}` : '';
  const shareText  = t('dashboard.preview.share.copy-text-content', { url: shareUrl });

  const qrWasGenerated = localStorage.getItem(QR_STORAGE_KEY) !== null;
  const qrButtonLabel  = qrVisible
    ? t('dashboard.preview.share.qr-hide')
    : qrWasGenerated
      ? t('dashboard.preview.share.qr-show')
      : t('dashboard.preview.share.qr-generate');

  const themedQrBg = theme?.background?.type === 'gradient'
    ? (theme?.background?.gradient?.left ?? '#000000')
    : (theme?.background?.solid ?? '#000000');
  const themedQrFg = theme?.text?.primary ?? '#ffffff';
  const qrLowContrast = getContrastRatio(themedQrBg, themedQrFg) < QR_CONTRAST_MIN_RATIO;
  const qrBgColor = qrLowContrast ? QR_FALLBACK_BG : themedQrBg;
  const qrFgColor = qrLowContrast ? QR_FALLBACK_FG : themedQrFg;
  const qrLogoSrc = getRgbLuminance(qrBgColor) > 128 ? logoUrlDark : logoUrl;

  const handleCopy = useCallback((text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
  }, []);

  const handleToggleQr = useCallback(() => {
    setQrVisible(prev => {
      const next = !prev;
      localStorage.setItem(QR_STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  const handleDownloadQR = useCallback(() => {
    const canvas = document.getElementById('profile-qr-canvas') as HTMLCanvasElement;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.download = `linkerto-${username}-qr.png`;
    link.href = dataUrl;
    link.click();
  }, [username]);

  const profileContent = (
    <ProfileView
      className="preview-profile-content"
      links={links}
      theme={theme}
      info={info}
      username={username}
      origin="preview"
      loading={loading}
      avatar={avatarSrc ? <Avatar src={avatarSrc} /> : undefined}
    />
  );

  return (
    <Box className="preview-tab">
      {/* Header */}
      <Box className="preview-tab-header">
        <Box>
          <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, lineHeight: 1.3 }}>
              {t('dashboard.preview.subtitle')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
            {t('dashboard.preview.description')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {loading
            ? <Skeleton variant="rounded" width={160} height={14} />
            : <PreviewStatus
                pending={pending}
                publishDate={state.publishDate}
                profileStatus={profileStatus}
                t={t}
              />
          }
          {!loading && state.isPublished && !hasChanges && (
            <Tooltip title={t('dashboard.preview.open-profile')}>
              <IconButton
                size="medium"
                component="a"
                href={profileUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: 'text.secondary' }}
              >
                <TbLink size={16} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Body — two columns */}
      <Box className="preview-body">
        {/* Left: device toggle + frame */}
        <Box className="preview-left" data-device={device}>
          <Box className="preview-device-toggle" sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <ToggleButtonGroup
              value={device}
              exclusive
              onChange={(_, val) => val && setDevice(val)}
              size="small"
            >
              <ToggleButton value="mobile" sx={{ gap: 1, px: 2.5 }}>
                <FiSmartphone size={15} />
                {t('dashboard.preview.device-mobile')}
              </ToggleButton>
              <ToggleButton value="desktop" sx={{ gap: 1, px: 2.5 }}>
                <FiMonitor size={15} />
                {t('dashboard.preview.device-desktop')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <div className="preview-frame-phone preview-frame-container">
            <div className="phone-frame-wrapper preview-phone-wrapper">
              <div className="phone-frame preview-phone-frame">
                {profileContent}
                <div className="phone-home-bar">
                  <div className="phone-home-bar-indicator">
                    <Typography variant="caption" sx={{ color: 'text.primary', opacity: 0.7 }}>linkerto.me</Typography>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="preview-frame-desktop preview-frame-container">
            <Paper
              variant="outlined"
              className="preview-desktop-frame"
              sx={{ borderColor: 'rgba(255,255,255,0.1)', borderRadius: 1, overflow: 'hidden' }}
            >
              <Box sx={{
                background: 'rgba(255,255,255,0.03)',
                borderBottom: '1px solid rgba(255,255,255,0.07)',
                px: 2, py: 1,
                display: 'flex', alignItems: 'center', gap: 1.5,
              }}>
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                  {['#ff5f57', '#febc2e', '#28c840'].map(c => (
                    <Box key={c} sx={{ width: 10, height: 10, borderRadius: '50%', background: c, opacity: 0.55 }} />
                  ))}
                </Box>
                <Box sx={{
                  flex: 1, mx: 1,
                  background: 'rgba(255,255,255,0.06)',
                  borderRadius: 1, px: 1.5, py: 0.4,
                }}>
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontFamily: 'monospace' }}>
                    linkerto.me/{username}
                  </Typography>
                </Box>
              </Box>
              {profileContent}
            </Paper>
          </div>
        </Box>

        {/* Right: share hub */}
        <Box className="preview-right">
          <Paper
            variant="outlined"
            className="preview-share-card"
            sx={{ borderColor: 'rgba(255,255,255,0.08)', p: 3 }}
          >
            <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, mb: 2.5 }}>
              {t('dashboard.preview.share.title')}
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Skeleton variant="rounded" height={44} sx={{ borderRadius: '8px' }} />
                <Skeleton variant="text" width="35%" height={14} sx={{ mt: 1 }} />
                <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
                  {[1, 2, 3, 4, 5, 6, 7].map(i => (
                    <Skeleton key={i} variant="rounded" width={40} height={40} sx={{ borderRadius: '8px' }} />
                  ))}
                </Box>
                <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.07)', pt: 2.5, mt: 1 }}>
                  <Skeleton variant="text" width="40%" height={18} />
                </Box>
              </Box>
            ) : !state.isPublished ? (
              <Box className="preview-share-locked">
                <Box className="preview-share-locked-icon">
                  <MdLockOutline size={28} />
                </Box>
                <Typography variant="body1" className="preview-share-locked-title" sx={{ color: 'text.primary' }}>
                  {t('dashboard.preview.share.locked-title')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                  {t('dashboard.preview.share.locked-subtitle')}
                </Typography>
                <Tooltip title={isInDeletion ? t('dashboard.main.deletion-alert.guard-tooltip') : ''}>
                  <span style={{ width: '100%' }}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={deploy}
                      disabled={pending || isInDeletion}
                      sx={{ mt: 2 }}
                    >
                      {t('dashboard.preview.share.publish-cta')}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            ) : (
              <>
                {/* Primary action: copy link */}
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<MdLink size={18} />}
                  disabled={!username || !window.isSecureContext}
                  onClick={() => handleCopy(profileUrl)}
                  className="preview-share-copy-btn"
                >
                  {t('dashboard.preview.share.copy-url')}
                </Button>

                {/* Secondary: brand chips */}
                <Typography
                  variant="caption"
                  className="preview-share-networks-label"
                  sx={{ color: 'text.disabled' }}
                >
                  {t('dashboard.preview.share.networks-label')}
                </Typography>
                <div className="preview-share-chips">
                  {SHARE_NETWORKS.map(network => (
                    <Tooltip key={network.id} title={t(`dashboard.preview.share.networks.${network.id}`)}>
                      <IconButton
                        className="share-btn"
                        component="a"
                        href={buildShareUrl(network.id, profileUrl, shareText)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ '--share-color': network.color } as React.CSSProperties}
                      >
                        <IconContext.Provider value={{ size: '17px' }}>
                          {NETWORK_ICONS[network.id]}
                        </IconContext.Provider>
                      </IconButton>
                    </Tooltip>
                  ))}
                </div>

                {/* QR Code */}
                <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.07)', pt: 2.5, mt: 2.5 }}>
                  <Box className="preview-qr-section-header">
                    <Typography
                      variant="caption"
                      className="preview-share-networks-label"
                      sx={{ color: 'text.disabled' }}
                    >
                      {t('dashboard.preview.share.qr-section-title')}
                    </Typography>
                  </Box>
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<MdOutlineQrCode2 size={18} />}
                    onClick={handleToggleQr}
                    sx={{ color: 'text.secondary', mb: qrVisible ? 2 : 0 }}
                  >
                    {qrButtonLabel}
                  </Button>

                  <AnimatePresence>
                    {qrVisible && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.22 }}
                        style={{ overflow: 'hidden' }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, pt: 1 }}>
                          <Box sx={{ p: 1.5, background: qrBgColor, borderRadius: 1, display: 'inline-flex' }}>
                            <QRCodeCanvas
                              id="profile-qr-canvas"
                              value={profileUrl || `https://${PUBLIC_PROFILE_BASE}`}
                              size={180}
                              level="H"
                              bgColor={qrBgColor}
                              fgColor={qrFgColor}
                              imageSettings={{
                                src: qrLogoSrc,
                                width: 50,
                                height: 50,
                                excavate: true,
                              }}
                            />
                          </Box>
                          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                            {t('dashboard.preview.share.qr-subtitle')}
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<MdDownload />}
                            onClick={handleDownloadQR}
                          >
                            {t('dashboard.preview.share.download-qr')}
                          </Button>
                        </Box>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Box>
              </>
            )}
          </Paper>
        </Box>
      </Box>

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" sx={{ width: '100%' }}>
          {t('dashboard.preview.share.copied')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Preview;
